import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../components/TopBar";
import { api, fmtUSD } from "../lib/api";
import { useYear } from "../context/YearContext";
import { useBusiness } from "../context/BusinessContext";
import { Sprout, Wrench, Truck, ArrowRight } from "lucide-react";

const ICONS = { farm: Sprout, repair: Wrench, trucking: Truck };
const PATHS = { farm: "/", jk_repair: "/repair", jk_trucking: "/trucking" };

export default function Hub() {
  const { year } = useYear();
  const { businesses, setActive } = useBusiness();
  const [perBusiness, setPerBusiness] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [allInv, allExp, farmSummary] = await Promise.all([
        api.get(`/invoices?year=${year}`),
        api.get(`/expenses?year=${year}`),
        api.get(`/reports/summary?year=${year}`),
      ]);
      const map = {};
      // Repair + Trucking (invoice-based revenue)
      for (const b of businesses.filter((b) => b.type !== "farm")) {
        const invs = allInv.data.filter((i) => i.business_id === b.id);
        const revenue = invs.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);
        const outstanding = invs.filter((i) => ["sent", "overdue"].includes(i.status)).reduce((s, i) => s + (i.total || 0), 0);
        const expenses = allExp.data.filter((e) => (e.business_id || "farm") === b.id).reduce((s, e) => s + (e.amount || 0), 0);
        map[b.id] = { revenue, outstanding, expenses, net: revenue - expenses, invoice_count: invs.length };
      }
      // Farm (yield-based revenue + farm expenses)
      const farmExpenses = allExp.data.filter((e) => (e.business_id || "farm") === "farm").reduce((s, e) => s + (e.amount || 0), 0);
      map.farm = {
        revenue: farmSummary.data.total_revenue,
        outstanding: 0,
        expenses: farmExpenses,
        net: farmSummary.data.net_income,
        invoice_count: 0,
        meta: `${farmSummary.data.yield_count} harvests · ${farmSummary.data.field_count} fields`,
      };
      setPerBusiness(map);
    })();
  }, [year, businesses]);

  const totalNet = Object.values(perBusiness).reduce((s, b) => s + (b.net || 0), 0);
  const totalRevenue = Object.values(perBusiness).reduce((s, b) => s + (b.revenue || 0), 0);
  const totalOutstanding = Object.values(perBusiness).reduce((s, b) => s + (b.outstanding || 0), 0);

  return (
    <div>
      <TopBar title="Operations Hub" subtitle={`All three businesses at a glance — ${year}.`} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="hub-totals">
        <Stat label="Combined Revenue" value={fmtUSD(totalRevenue)} tone="positive" />
        <Stat label="Outstanding" value={fmtUSD(totalOutstanding)} tone="warning" />
        <Stat label="Combined Net" value={fmtUSD(totalNet)} tone={totalNet >= 0 ? "highlight" : "negative"} testid="hub-combined-net" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="hub-businesses">
        {businesses.map((b) => {
          const Icon = ICONS[b.type] || Sprout;
          const data = perBusiness[b.id] || { revenue: 0, expenses: 0, net: 0, outstanding: 0 };
          return (
            <button
              key={b.id}
              data-testid={`hub-card-${b.id}`}
              onClick={() => { setActive(b.id); navigate(PATHS[b.id] || "/"); }}
              className="bg-white border-2 border-[#1F1E1B] p-6 text-left hover:bg-[#FAF7EC] transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#1F1E1B] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                </div>
                <div className="flex-1">
                  <div className="font-display text-lg font-bold leading-tight">{b.name}</div>
                  {data.meta && <div className="text-xs text-[#66645E]">{data.meta}</div>}
                </div>
                <ArrowRight className="w-4 h-4 text-[#66645E]" />
              </div>

              <div className="space-y-1 text-sm">
                <Row label="Revenue" value={fmtUSD(data.revenue)} />
                {data.outstanding > 0 && <Row label="Outstanding" value={fmtUSD(data.outstanding)} tone="warning" />}
                <Row label="Expenses" value={fmtUSD(data.expenses)} tone="muted" />
                <div className="pt-2 mt-2 border-t border-[#1F1E1B] flex justify-between items-baseline">
                  <span className="text-xs font-bold uppercase tracking-wider">Net</span>
                  <span className={`font-display text-2xl font-black tabular ${data.net >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>{fmtUSD(data.net)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const color = tone === "positive" ? "text-[#1A4135]" : tone === "warning" ? "text-[#BC4727]" : tone === "negative" ? "text-[#BC4727]" : "text-[#1F1E1B]";
  return (
    <div className="bg-white border border-[#1F1E1B] p-4" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-3xl font-black tabular mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, tone }) {
  const color = tone === "warning" ? "text-[#BC4727]" : tone === "muted" ? "text-[#66645E]" : "text-[#1F1E1B]";
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[#66645E]">{label}</span>
      <span className={`tabular font-semibold ${color}`}>{value}</span>
    </div>
  );
}
